import React from "react";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useGetJobMetricSeriesQuery } from "~/shared/api/analytics/analytics.grpc";
import { DateValue } from "~/shared/lib/analytics-utils";
import { AnalyticsLineChart } from "./AnalyticsLineChart";

interface Props {
  startDate: DateValue;
  endDate: DateValue;
  jobCategory: ConnectProto.JobCategory;
}

export const BusinessMetricsChart: React.FC<Props> = ({ startDate, endDate, jobCategory }) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();

  const { data, isLoading, isFetching } = useGetJobMetricSeriesQuery(
    {
      ownerOrganizationId: BigInt(selectedCompanyId || 0),
      period: { from: startDate.toString(), to: endDate.toString() },
      metric: ConnectProto.JobMetric.REVENUE,
      jobCategory,
    },
    { skip: !selectedCompanyId },
  );

  const totalThousands = Number(data?.total ?? 0n) / 1000;

  return (
    <div className="h-full flex flex-col border rounded-xl bg-white p-4">
      <div className="mb-2">
        <h3 className="text-lg text-gray-900 font-bold">
          {t(Namespace.Analytics, "charts.business.title")}
        </h3>
        <p className="mb-1 mt-3 text-sm font-semibold">
          {t(Namespace.Analytics, "charts.business.subtitle")}:{" "}
          <span className="text-blue-600">
            {totalThousands.toFixed(0)} {t(Namespace.Analytics, "charts.business.thousandCurrency")}
          </span>
        </p>
      </div>

      <div className="min-h-0 flex-1">
        <AnalyticsLineChart
          series={[
            {
              label: t(Namespace.Analytics, "charts.business.revenue"),
              perDay: (data?.perDay ?? []).map((p) => ({
                date: p.date ?? "",
                value: Number(p.value ?? 0n) / 1000,
              })),
            },
          ]}
          startDate={startDate}
          endDate={endDate}
          isLoading={isLoading || isFetching}
          valueSuffix={t(Namespace.Analytics, "charts.business.thousandCurrency")}
          total={`${totalThousands.toFixed(0)} ${t(Namespace.Analytics, "charts.business.thousandCurrency")}`}
        />
      </div>
    </div>
  );
};
